import { Link } from 'react-router-dom';
import { PortalBreadcrumb } from './PortalBreadcrumb';
import { PortalTopbar } from './PortalTopbar';
import { SystemMapCanvas } from './SystemMapCanvas';
import { SystemMapControls, type StopStatusFilter } from './SystemMapControls';
import { SystemMapEdgePanel } from './SystemMapEdgePanel';
import { SystemMapNewStopPanel } from './SystemMapNewStopPanel';
import { SystemMapPanel } from './SystemMapPanel';
import { SystemMapReviewBar } from './SystemMapReviewBar';
import type { SystemMapRouteGeometry, SystemMapStopDetail, SystemMapStopsResponse, UrbanNetworkEdge } from './api';
import type { LoadState, MapSelectionState, NewStopComputed } from './systemMapPageHooks';
import type { NewStopDraft, ReviewWorkflowState, StopOverride } from './systemMapReview';

/** Encabezado de `/analysis/system-map` — extraído para que `SystemMapPage` no cargue este JSX estático. Reutiliza `PortalTopbar` (mismo logo/marca/usuario que el resto del portal) + un breadcrumb, arriba del título/contador ya existentes. */
export function SystemMapHeader({ stopsTotalLabel }: { stopsTotalLabel: string }): JSX.Element {
  return (
    <>
      <PortalTopbar />
      <header className="smap-topbar">
        <div>
          <PortalBreadcrumb
            segments={[
              { label: 'Inicio', to: '/' },
              { label: 'Sistema de Transporte', to: '/analysis' },
              { label: 'Mapa del sistema' },
            ]}
          />
          <div>
            <span className="smap-topbar-title">Mapa del sistema</span>
            <span className="smap-topbar-sub">
              21 rutas cerradas · {stopsTotalLabel} paradas · sin persistencia real
            </span>
          </div>
        </div>
        <Link to="/analysis" className="smap-back-link">
          ← Volver al Dashboard del sistema
        </Link>
      </header>
    </>
  );
}

/** Panel lateral derecho — decide entre "Nueva parada" (draft), detalle de parada o inspección de edge-solo. Extraído para que `SystemMapPage` no cargue este switch de tres ramas. */
export function SystemMapSidePanel({
  draft,
  draftComputed,
  onUpdateDraft,
  onClearManualOverride,
  onCancelDraft,
  selection,
  editing,
  sessionOverride,
  onToggleEdit,
  onCancelEdit,
  onConfirmInSession,
}: {
  draft: NewStopDraft | null;
  draftComputed: NewStopComputed;
  onUpdateDraft: (patch: Partial<NewStopDraft>) => void;
  onClearManualOverride: () => void;
  onCancelDraft: () => void;
  selection: MapSelectionState & {
    detail: SystemMapStopDetail | null;
    detailLoading: boolean;
    detailError: string | null;
  };
  editing: boolean;
  sessionOverride: StopOverride | undefined;
  onToggleEdit: () => void;
  onCancelEdit: () => void;
  onConfirmInSession: () => void;
}): JSX.Element | null {
  if (draft) {
    return (
      <SystemMapNewStopPanel
        draft={draft}
        proposedEdge={draftComputed.proposedEdge}
        manualEdge={draftComputed.manualEdge}
        distanceM={draftComputed.distanceM}
        nearestExisting={draftComputed.nearestExisting}
        onUpdate={onUpdateDraft}
        onClearManualOverride={onClearManualOverride}
        onCancel={onCancelDraft}
      />
    );
  }
  if (selection.selectedStopId) {
    return (
      <SystemMapPanel
        detail={selection.detail}
        loading={selection.detailLoading}
        error={selection.detailError}
        candidate={
          selection.candidateEdge ? { edge: selection.candidateEdge, distanceM: selection.candidateDistanceM } : null
        }
        editing={editing}
        sessionOverride={sessionOverride}
        onClose={selection.closeStopPanel}
        onClearCandidate={selection.clearCandidate}
        onToggleEdit={onToggleEdit}
        onCancelEdit={onCancelEdit}
        onConfirmInSession={onConfirmInSession}
      />
    );
  }
  if (selection.edgeOnly) {
    return <SystemMapEdgePanel edge={selection.edgeOnly} onClose={selection.closeEdgeOnlyPanel} />;
  }
  return null;
}

/** Canvas + controles — extraído para mantener `SystemMapPage` corto; agrupa todo lo que solo se renderiza cuando `loadState==='ready'`. */
export function SystemMapCanvasArea({
  routes,
  stops,
  visibleStops,
  selectedLineId,
  onSelectLineId,
  activeStatuses,
  onToggleStatus,
  onReset,
  urbanNetworkEnabled,
  urbanNetworkEdges,
  urbanNetworkLoading,
  onToggleUrbanNetwork,
  selection,
  onSelectEdge,
  review,
  draftMode,
  draftPoint,
  draftEdgeGeometry,
  sessionOverrideIds,
  onPlaceDraft,
  onStartReview,
  newStopArmed,
  onToggleNewStop,
}: {
  routes: SystemMapRouteGeometry[];
  stops: SystemMapStopsResponse;
  visibleStops: SystemMapStopsResponse['stops'];
  selectedLineId: string | null;
  onSelectLineId: (id: string | null) => void;
  activeStatuses: Set<StopStatusFilter>;
  onToggleStatus: (status: StopStatusFilter) => void;
  onReset: () => void;
  urbanNetworkEnabled: boolean;
  urbanNetworkEdges: UrbanNetworkEdge[] | null;
  urbanNetworkLoading: boolean;
  onToggleUrbanNetwork: () => void;
  selection: MapSelectionState & { detail: SystemMapStopDetail | null };
  onSelectEdge: (edgeId: string) => void;
  review: ReviewWorkflowState;
  draftMode: boolean;
  draftPoint: [number, number] | null;
  draftEdgeGeometry: GeoJSON.LineString | null;
  sessionOverrideIds: string[];
  onPlaceDraft: (lon: number, lat: number) => void;
  onStartReview: () => void;
  newStopArmed: boolean;
  onToggleNewStop: () => void;
}): JSX.Element {
  return (
    <>
      <SystemMapCanvas
        routes={routes}
        stops={visibleStops}
        selectedLineId={selectedLineId}
        selectedStopId={selection.selectedStopId}
        stopPoint={selection.stopPoint}
        edgeGeometry={selection.detail?.edgeGeometry ?? null}
        projectedPoint={selection.detail?.projectedCoordinate ?? null}
        urbanNetworkEnabled={urbanNetworkEnabled}
        urbanNetworkEdges={urbanNetworkEdges}
        candidateEdgeGeometry={selection.candidateEdge?.geometry ?? null}
        reviewFocusToken={review.focusToken}
        draftModeArmed={draftMode}
        draftPoint={draftPoint}
        draftEdgeGeometry={draftEdgeGeometry}
        sessionOverrideIds={sessionOverrideIds}
        onSelectStop={selection.selectStop}
        onSelectEdge={onSelectEdge}
        onPlaceDraft={onPlaceDraft}
      />
      <SystemMapControls
        routes={routes}
        selectedLineId={selectedLineId}
        onSelectLineId={onSelectLineId}
        activeStatuses={activeStatuses}
        onToggleStatus={onToggleStatus}
        visibleCount={visibleStops.length}
        counts={stops.counts}
        onReset={onReset}
        urbanNetworkEnabled={urbanNetworkEnabled}
        urbanNetworkLoading={urbanNetworkLoading}
        onToggleUrbanNetwork={onToggleUrbanNetwork}
        reviewActive={review.active}
        onStartReview={onStartReview}
        newStopArmed={newStopArmed}
        onToggleNewStop={onToggleNewStop}
      />
    </>
  );
}

type CanvasAreaOwnProps = Parameters<typeof SystemMapCanvasArea>[0];

/** Envuelve el estado de carga (loading/error/ready) + la barra de revisión — saca esas ramas de `SystemMapPage`, que ya no necesita saber nada de `loadState`. */
export function SystemMapMapWrap({
  loadState,
  stops,
  canvasAreaProps,
  review,
  stopName,
  confirmed,
  editing,
  onConfirm,
  onEditAssociation,
}: {
  loadState: LoadState;
  stops: SystemMapStopsResponse | null;
  canvasAreaProps: Omit<CanvasAreaOwnProps, 'stops'>;
  review: ReviewWorkflowState;
  stopName: string | null;
  confirmed: boolean;
  editing: boolean;
  onConfirm: () => void;
  onEditAssociation: () => void;
}): JSX.Element {
  return (
    <div className="smap-map-wrap">
      {loadState === 'loading' && <div className="smap-loading-overlay">Cargando mapa del sistema…</div>}
      {loadState === 'error' && (
        <div className="smap-loading-overlay">No se pudo cargar el mapa. Reintentá en unos segundos.</div>
      )}
      {loadState === 'ready' && stops && <SystemMapCanvasArea stops={stops} {...canvasAreaProps} />}
      <SystemMapReviewBar
        review={review}
        stopName={stopName}
        confirmed={confirmed}
        editing={editing}
        onConfirm={onConfirm}
        onEditAssociation={onEditAssociation}
      />
    </div>
  );
}
